import * as XLSX from "xlsx";
import Papa from "papaparse";
import CurrencyConverter from "../Currency/CurrencyConverter";

const STRIPE_SUB_TYPE = "stripe-subscription-report";

const cycleHeaders = ["Subscription ID", "Invoice ID", "Amount", "Period Start", "Period End", "Status", "Billing Reason", "Date"];

const toTitleCase = (str) =>
    str ? str.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : str;

const formatRow = (obj, currencySymbol, conversionFactor) => {
    const formattedObj = { ...obj };
    if (formattedObj.totalAmount != null) {
        formattedObj.totalAmount = `${currencySymbol} ${CurrencyConverter(formattedObj.totalAmount, conversionFactor)}`;
    }
    if (formattedObj.amount != null) {
        formattedObj.amount = `${currencySymbol} ${CurrencyConverter(formattedObj.amount, conversionFactor)}`;
    }
    if (formattedObj.fee != null) {
        formattedObj.fee = `${currencySymbol} ${CurrencyConverter(formattedObj.fee, conversionFactor)}`;
    }
    return formattedObj;
};

const buildStripeSubscriptionSheet = (data, headers, currencySymbol, conversionFactor) => {
    const exportHeaders = headers[0] === "" ? headers.slice(1) : headers;
    const rows = data.map(obj => {
        const { recurringCycles, ...rest } = obj;
        return Object.values(formatRow(rest, currencySymbol, conversionFactor));
    });
    return [exportHeaders, ...rows];
};

const buildStripeInvoiceSheet = (data, currencySymbol, conversionFactor) => {
    const rows = [];
    data.forEach(obj => {
        if (obj.recurringCycles?.length > 0) {
            obj.recurringCycles.forEach(cycle => {
                rows.push([
                    obj.subscriptionId,
                    cycle.invoiceId,
                    `${currencySymbol} ${CurrencyConverter(cycle.amount, conversionFactor)}`,
                    cycle.periodStart,
                    cycle.periodEnd,
                    toTitleCase(cycle.status),
                    toTitleCase(cycle.billingReason),
                    cycle.date,
                ]);
            });
        }
    });
    return [cycleHeaders, ...rows];
};

const buildStripeCSVRows = (data, headers, currencySymbol, conversionFactor) => {
    const exportHeaders = headers[0] === "" ? headers.slice(1) : headers;
    const rows = [exportHeaders];
    data.forEach(obj => {
        const { recurringCycles, ...rest } = obj;
        rows.push(Object.values(formatRow(rest, currencySymbol, conversionFactor)));
        if (recurringCycles?.length > 0) {
            rows.push(cycleHeaders);
            recurringCycles.forEach(cycle => {
                rows.push([
                    obj.subscriptionId,
                    cycle.invoiceId,
                    `${currencySymbol} ${CurrencyConverter(cycle.amount, conversionFactor)}`,
                    cycle.periodStart,
                    cycle.periodEnd,
                    toTitleCase(cycle.status),
                    toTitleCase(cycle.billingReason),
                    cycle.date,
                ]);
            });
            rows.push([]);
        }
    });
    return rows;
};

export const exportToExcel = async (data, headers, type, currencySymbol, conversionFactor) => {
    const wb = XLSX.utils.book_new();

    if (type === STRIPE_SUB_TYPE) {
        const subSheet = XLSX.utils.aoa_to_sheet(buildStripeSubscriptionSheet(data, headers, currencySymbol, conversionFactor));
        XLSX.utils.book_append_sheet(wb, subSheet, "Subscriptions");

        const invoiceSheet = XLSX.utils.aoa_to_sheet(buildStripeInvoiceSheet(data, currencySymbol, conversionFactor));
        XLSX.utils.book_append_sheet(wb, invoiceSheet, "Invoices");
    } else {
        const wsData = data.map(obj => Object.values(formatRow(obj, currencySymbol, conversionFactor)));
        const ws = XLSX.utils.aoa_to_sheet([headers, ...wsData]);
        XLSX.utils.book_append_sheet(wb, ws, "data");
    }

    XLSX.writeFile(wb, `${type}.xlsx`);
};

export const exportToCSV = async (data, headers, type, currencySymbol, conversionFactor) => {
    let rows;
    if (type === STRIPE_SUB_TYPE) {
        rows = buildStripeCSVRows(data, headers, currencySymbol, conversionFactor);
    } else {
        const wsData = data.map(obj => Object.values(formatRow(obj, currencySymbol, conversionFactor)));
        rows = [headers, ...wsData];
    }
    const csvData = Papa.unparse(rows);
    const blob = new Blob([csvData], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${type}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};
